/*!
 * schnorr.js - bip-schnorr for bcrypto
 * Copyright (c) 2019, Christopher Jeffrey (MIT License).
 * https://github.com/bcoin-org/bcrypto
 *
 * Parts of this software are based on sipa/bip-schnorr:
 *   https://github.com/sipa/bips/blob/bip-schnorr/bip-schnorr/reference.py
 *
 * Parts of this software are based on ElementsProject/secp256k1-zkp:
 *   https://github.com/ElementsProject/secp256k1-zkp/tree/secp256k1-zkp/src/modules/schnorrsig
 *
 * Resources:
 *   https://github.com/sipa/bips/blob/bip-schnorr/bip-schnorr.mediawiki
 *   https://github.com/sipa/bips/blob/bip-schnorr/bip-schnorr/reference.py
 *   https://github.com/sipa/bips/blob/bip-schnorr/bip-schnorr/test-vectors.csv
 *   https://github.com/ElementsProject/secp256k1-zkp
 *   https://github.com/ElementsProject/secp256k1-zkp/tree/secp256k1-zkp/src/modules/musig
 *   https://github.com/ElementsProject/secp256k1-zkp/tree/secp256k1-zkp/src/modules/schnorrsig
 */
'use strict';

var _slicedToArray = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/slicedToArray");

var _classCallCheck = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/classCallCheck");

var _createClass = require("/opt/build/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/createClass");

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

var assert = require('bsert');

var rng = require('../random');

var BN = require('../bn.js');
/**
 * Schnorr
 */


var Schnorr = /*#__PURE__*/function () {
  function Schnorr(curve, hash) {
    _classCallCheck(this, Schnorr);

    this.curve = curve;
    this.hash = hash;
  }

  _createClass(Schnorr, [{
    key: "hashInt",
    value: function hashInt() {
      // eslint-disable-next-line
      var h = new this.hash();
      h.init();

      for (var _len = arguments.length, items = new Array(_len), _key = 0; _key < _len; _key++) {
        items[_key] = arguments[_key];
      }

      for (var _i = 0, _items = items; _i < _items.length; _i++) {
        var item = _items[_i];
        h.update(item);
      }

      var hash = h.final();
      var num = BN.decode(hash, this.curve.endian);
      return num.iumod(this.curve.n);
    }
  }, {
    key: "sign",
    value: function sign(msg, key) {
      assert(Buffer.isBuffer(msg));
      assert(Buffer.isBuffer(key));
      assert(msg.length === this.hash.size);
      assert(key.length === this.curve.size);
      var N = this.curve.n;
      var G = this.curve.g; // Let k' = int(hash(bytes(d) || m)) mod n

      var k = this.hashInt(key, msg); // Fail if k' = 0.

      if (k.isZero()) throw new Error('Signing failed (k\' = 0).'); // Let R = k'*G.

      var R = G.mulBlind(k); // Let k = k' if jacobi(y(R)) = 1, otherwise let k = n - k'.

      if (R.y.redJacobi() !== 1) k = N.sub(k).iumod(N); // Encode x(R).

      var Rraw = this.curve.encodeInt(R.getX()); // The secret key d: an integer in the range 1..n-1.

      var a = this.curve.decodeScalar(key);
      if (a.isZero() || a.cmp(N) >= 0) throw new Error('Invalid private key.'); // Encode d*G.

      var Araw = G.mulBlind(a).encode(); // Let e = int(hash(bytes(x(R)) || bytes(d*G) || m)) mod n.

      var e = this.hashInt(Rraw, Araw, msg); // Blinding factor (precomputed).

      var _this$curve$getBlindi = this.curve.getBlinding(),
          _this$curve$getBlindi2 = _slicedToArray(_this$curve$getBlindi, 2),
          b = _this$curve$getBlindi2[0],
          bi = _this$curve$getBlindi2[1]; // ea := (e * a) mod n (unblinded)
      // ea := (b * a * e) mod n (blinded)


      var ea = b.mul(a).iumod(N).imul(e).iumod(N); // s := (k + (e * a)) mod n (unblinded)
      // s := ((b * k + (b * a * e)) * b^-1) mod n (blinded)

      var S = b.mul(k).iumod(N).iadd(ea).iumod(N).imul(bi).iumod(N); // The signature is bytes(x(R)) || bytes(k + e*d mod n).

      return Buffer.concat([Rraw, this.curve.encodeScalar(S)]);
    }
  }, {
    key: "verify",
    value: function verify(msg, sig, key) {
      assert(Buffer.isBuffer(msg));
      assert(Buffer.isBuffer(sig));
      assert(Buffer.isBuffer(key));
      if (msg.length !== this.hash.size) return false;
      if (sig.length !== this.curve.size * 2) return false;

      try {
        return this._verify(msg, sig, key);
      } catch (e) {
        return false;
      }
    }
  }, {
    key: "_verify",
    value: function _verify(msg, sig, key) {
      assert(Buffer.isBuffer(msg));
      assert(Buffer.isBuffer(sig));
      assert(Buffer.isBuffer(key));
      var P = this.curve.p;
      var N = this.curve.n;
      var G = this.curve.g; // Let r = int(sig[0:32]); fail if r >= p.
      // Let s = int(sig[32:64]); fail if s >= n.
      // Let P = point(pk); fail if point(pk) fails.

      var Rraw = sig.slice(0, this.curve.size);
      var Sraw = sig.slice(this.curve.size);
      var Rx = this.curve.decodeInt(Rraw);
      var S = this.curve.decodeScalar(Sraw);
      var A = this.curve.decodePoint(key);
      if (Rx.cmp(P) >= 0 || S.cmp(N) >= 0) return false; // Let e = int(hash(bytes(r) || bytes(P) || m)) mod n.

      var e = this.hashInt(Rraw, A.encode(), msg); // In concept, a schnorr sig can be validated with:
      //
      //   s*G == R + e*P
      //
      // But bip-schnorr optimizes for shamir's trick with:
      //
      //   r == x(s*G - e*P)
      //
      // This is even more necessary perf-wise since we only
      // encode the X coordinate as the R value (it avoids us
      // having to recalculate the Y coordinate).
      //
      // Note that we stay in the jacobian space here. This
      // avoids any unnecessary divisions by the Z coordinate.
      // Let R = s*G - e*P.
      // Fail if infinite(R) or jacobi(y(R)) != 1 or x(R) != r.

      var R = G.jmulAdd(S, A, N.sub(e).iumod(N)); // Check for point at infinity.

      if (R.isInfinity()) return false; // Check for quadratic residue in the jacobian space.
      // Optimized as `jacobi(y(R) * z(R)) == 1`.

      if (!R.hasQuadY()) return false; // Check `x(R) == r` in the jacobian space.
      // Optimized as `x(R) == r * z(R)^2 mod p`.

      if (!R.eqX(Rx)) return false;
      return true;
    }
  }, {
    key: "batchVerify",
    value: function batchVerify(batch) {
      assert(Array.isArray(batch));

      var _iterator = _createForOfIteratorHelper(batch),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var item = _step.value;
          assert(Array.isArray(item) && item.length === 3);

          var _item = _slicedToArray(item, 3),
              msg = _item[0],
              sig = _item[1],
              key = _item[2];

          assert(Buffer.isBuffer(msg));
          assert(Buffer.isBuffer(sig));
          assert(Buffer.isBuffer(key));
          if (msg.length !== this.hash.size) return false;
          if (sig.length !== this.curve.size * 2) return false;
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      try {
        return this._batchVerify(batch);
      } catch (e) {
        return false;
      }
    }
  }, {
    key: "_batchVerify",
    value: function _batchVerify(batch) {
      var P = this.curve.p;
      var N = this.curve.n;
      var G = this.curve.g;
      var points = [];
      var coeffs = [];
      var sum = null;

      var _iterator2 = _createForOfIteratorHelper(batch),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _step2$value = _slicedToArray(_step2.value, 3),
              msg = _step2$value[0],
              sig = _step2$value[1],
              key = _step2$value[2];

          // Let r = int(sigi[0:32]); fail if r >= p.
          // Let si = int(sigi[32:64]); fail if si >= n.
          // Let Pi = point(pki); fail if point(pki) fails.
          var Rraw = sig.slice(0, this.curve.size);
          var Sraw = sig.slice(this.curve.size);
          var Rx = this.curve.decodeInt(Rraw);
          var S = this.curve.decodeScalar(Sraw);
          var A = this.curve.decodePoint(key);
          if (Rx.cmp(P) >= 0 || S.cmp(N) >= 0) return false; // Let ei = int(hash(bytes(r) || bytes(Pi) || mi)) mod n.

          var e = this.hashInt(Rraw, A.encode(), msg); // Let c = (r^3 + 7) mod p.
          // Let y = c^((p+1)/4) mod p.
          // Fail if c != y^2 mod p.
          // Let Ri = (r, y).

          var R = this.curve.pointFromR(Rx); // Let lhs = s1 + a2*s2 + ... + au*su.
          // Let rhs = R1 + a2*R2 + ... + au*Ru
          //         + e1*P1 + (a2*e2)P2 + ... + (au*eu)Pu.

          if (sum === null) {
            sum = S;
            points.push(R, A);
            coeffs.push(new BN(1), e);
            continue;
          } // Generate u-1 random integers a2...u in the range 1...n-1.


          var a = BN.random(rng, 1, N);
          var ea = e.imul(a).iumod(N);
          sum.iadd(S.imul(a)).iumod(N);
          points.push(R, A);
          coeffs.push(a, ea);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }

      if (sum === null) return true; // Fail if lhs*G != rhs.

      var lhs = G.jmul(sum);
      var rhs = this.curve.jmulAll(points, coeffs);
      return lhs.eq(rhs);
    }
  }]);

  return Schnorr;
}();
/*
 * Expose
 */


module.exports = Schnorr;